import { Card, CardHeader } from '../../components/ui/Card';
import { AsyncBoundary, EmptyState, SkeletonRows } from '../../components/ui/AsyncBoundary';
import { Badge } from '../../components/ui/Badge';
import { Icon } from '../../components/ui/Icon';
import { useResource } from '../../hooks/useResource';
import * as fmt from '../../lib/format';

/**
 * One timeline out of three sources — alerts, device events and trips.
 *
 * This is the view that answers "what happened to this vehicle?", which is
 * almost never answerable from one table. The server merges the three; this
 * only has to draw them consistently.
 */

const SOURCE_META = {
  alert: { icon: 'bell', tone: 'critical', label: 'Alert' },
  device: { icon: 'chip', tone: 'brand', label: 'Device' },
  trip: { icon: 'route', tone: 'neutral', label: 'Trip' },
};

const DEVICE_EVENT_LABELS = {
  connect: 'Device connected',
  disconnect: 'Device went offline',
  assigned: 'Device fitted to this vehicle',
  unassigned: 'Device removed',
  firmware_change: 'Firmware updated',
  hardware_model_change: 'Hardware model changed',
  iccid_change: 'SIM changed',
  vendor_id_change: 'Vendor changed',
  command_queued: 'Command sent to device',
  trip_start: 'Trip started',
};

export function EventsTab({ vehicleId }) {
  const events = useResource(`/api/vehicles/${vehicleId}/events`, { limit: 150 });

  return (
    <Card className="overflow-hidden">
      <CardHeader title="Timeline" subtitle="Alerts, device events and trips" icon="history" />
      <AsyncBoundary
        state={events}
        skeleton={<SkeletonRows rows={6} />}
        isEmpty={(data) => !data.events?.length}
        empty={
          <EmptyState
            icon="history"
            title="Nothing recorded yet"
            description="Events appear here as the device connects, reports and raises alerts."
          />
        }
      >
        {(data) => (
          <ol className="relative px-4 py-3">
            {/* the spine */}
            <span
              className="absolute left-[27px] top-4 bottom-4 w-px bg-line"
              aria-hidden="true"
            />

            {data.events.map((event, index) => {
              const meta = SOURCE_META[event.source] || SOURCE_META.device;
              const tone = event.severity
                ? event.severity === 'CRITICAL'
                  ? 'critical'
                  : event.severity === 'WARNING'
                    ? 'warning'
                    : 'brand'
                : meta.tone;

              return (
                <li key={`${event.t}-${index}`} className="relative flex gap-3 py-2">
                  <span
                    className={`relative z-10 flex h-6 w-6 shrink-0 items-center justify-center rounded-full border-2 border-surface ${
                      tone === 'critical'
                        ? 'bg-critical-soft text-critical-ink'
                        : tone === 'warning'
                          ? 'bg-warning-soft text-warning-ink'
                          : tone === 'brand'
                            ? 'bg-brand-soft text-brand-ink'
                            : 'bg-surface-2 text-ink-3'
                    }`}
                  >
                    <Icon name={meta.icon} size="xs" strokeWidth={2} />
                  </span>

                  <div className="min-w-0 flex-1 pb-1">
                    <div className="flex flex-wrap items-baseline gap-x-2">
                      <p className="text-[13.5px] font-medium text-ink">
                        {DEVICE_EVENT_LABELS[event.kind] || humanise(event.kind)}
                      </p>
                      {event.severity && (
                        <Badge tone={tone} size="sm">
                          {event.severity.toLowerCase()}
                        </Badge>
                      )}
                      <span className="text-[11.5px] text-ink-3" title={fmt.dateTime(event.t)}>
                        {fmt.relativeTime(event.t)}
                      </span>
                    </div>
                    {event.detail && (
                      <p className="mt-0.5 truncate text-[12.5px] text-ink-3">{cleanDetail(event.detail)}</p>
                    )}
                  </div>
                </li>
              );
            })}
          </ol>
        )}
      </AsyncBoundary>
    </Card>
  );
}

function humanise(kind) {
  if (!kind) return 'Event';
  if (kind.startsWith('rule:')) return 'Alert rule triggered';
  return kind.replace(/_/g, ' ').replace(/^./, (c) => c.toUpperCase());
}

/** Device details arrive as JSON text; show the readable part, not the braces. */
function cleanDetail(detail) {
  if (typeof detail !== 'string') return String(detail);
  if (!detail.startsWith('{')) return detail;
  try {
    const parsed = JSON.parse(detail);
    return Object.entries(parsed)
      .filter(([, value]) => value !== null && value !== undefined && value !== '')
      .map(([key, value]) => `${key.replace(/_/g, ' ')}: ${value}`)
      .join(' · ');
  } catch {
    return detail;
  }
}
